import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheck, FiSun, FiMoon, FiClock } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useToast } from '../contexts/ToastContext';
import { hapticImpact, hapticSelection } from '../services/hapticsService';

function TimelineAvailability({
  dateKey,
  dateLabel,
  timeRanges = [],
  onTimeRangesChange,
  mode = 'advanced', // Mode prop
  availabilityPreference = 'available', // Availability preference prop
  disabled = false // Disabled prop for calendar sync mode
}) {
  const toast = useToast();
  const timelineRef = useRef(null);
  const lastDisabledToastTime = useRef(0);
  const lastHourTickRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [draggedRange, setDraggedRange] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [hoveredTime, setHoveredTime] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [tapPosition, setTapPosition] = useState(null); // Position 0-1 where user tapped
  const [tapTime, setTapTime] = useState(null); // Formatted time string for badge
  const [touchStartTime, setTouchStartTime] = useState(null); // For timing detection
  const [touchStartPosition, setTouchStartPosition] = useState(null); // For movement detection
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null); // Slot awaiting delete confirmation
  const [isMoving, setIsMoving] = useState(false); // Moving a slot
  const [movingSlotIndex, setMovingSlotIndex] = useState(null); // Index of slot being moved
  const [moveOffset, setMoveOffset] = useState(0); // Offset from tap point to slot start
  const [movePreviewPosition, setMovePreviewPosition] = useState(null); // Preview position
  const [isResizing, setIsResizing] = useState(false); // Resizing a slot
  const [resizingSlotIndex, setResizingSlotIndex] = useState(null); // Index of slot being resized
  const [resizeEdge, setResizeEdge] = useState(null); // 'start' | 'end'
  const [resizePreview, setResizePreview] = useState(null); // { start, end, startPos, endPos }
  const [hoverZone, setHoverZone] = useState(null); // { type, index } for cursor changes
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null); // Keyboard-selected slot
  const [keyboardMode, setKeyboardMode] = useState(null); // null | 'move' | 'resize-start' | 'resize-end'
  const [announcement, setAnnouncement] = useState(''); // Screen reader announcements
  const announcementTimeoutRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Clean up announcement timeout on unmount
  useEffect(() => {
    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to make screen reader announcements
  const announce = useCallback((message) => {
    if (announcementTimeoutRef.current) {
      clearTimeout(announcementTimeoutRef.current);
    }
    setAnnouncement(message);
    announcementTimeoutRef.current = setTimeout(() => {
      setAnnouncement('');
    }, 1000);
  }, []);

  // Timeline configuration - full 24h day so every timezone / season is covered
  const START_HOUR = 0; // midnight
  const END_HOUR = 24; // midnight (end of day)
  const TOTAL_HOURS = END_HOUR - START_HOUR;

  // Edge detection threshold - larger on mobile for easier touch targeting
  const EDGE_THRESHOLD = isMobile ? 0.03 : 0.02; // 3% on mobile, 2% on desktop

  // Timing thresholds for distinguishing tap from drag
  const TAP_THRESHOLD_MS = 150; // Under 150ms = tap (delete)
  const MOVEMENT_THRESHOLD_PX = 10; // Movement over 10px = drag (move)

  // Convert time to position on timeline (0-1)
  const timeToPosition = useCallback((timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const totalMinutes = (hours - START_HOUR) * 60 + minutes;
    const maxMinutes = TOTAL_HOURS * 60;
    return Math.max(0, Math.min(1, totalMinutes / maxMinutes));
  }, []);

  // Convert position on timeline to time with hourly snapping
  const positionToTime = useCallback((position) => {
    const totalMinutes = position * TOTAL_HOURS * 60;
    const hours = Math.floor(totalMinutes / 60) + START_HOUR;
    // Snap to hourly increments only
    const roundedHours = Math.round(totalMinutes / 60) + START_HOUR;
    const clampedHours = Math.max(START_HOUR, Math.min(END_HOUR, roundedHours));
    return `${String(clampedHours).padStart(2, '0')}:00`;
  }, []);

  // Get position from mouse/touch event
  const getPositionFromEvent = useCallback((event) => {
    if (!timelineRef.current) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const x = clientX - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }, []);

  /**
   * Determine what interaction zone a position falls into
   * @param {number} position - Timeline position (0-1)
   * @param {Array} ranges - Current time ranges
   * @param {number} edgeThreshold - Position threshold for edge detection
   * @returns {{ type: 'resize-start' | 'resize-end' | 'slot-body' | 'empty', index: number }}
   */
  const getInteractionZone = useCallback((position, ranges, edgeThreshold = EDGE_THRESHOLD) => {
    // Check each range for hit detection
    for (let i = 0; i < ranges.length; i++) {
      const startPos = timeToPosition(ranges[i].start);
      const endPos = timeToPosition(ranges[i].end);

      // Check start edge first (left side)
      if (Math.abs(position - startPos) <= edgeThreshold) {
        return { type: 'resize-start', index: i };
      }

      // Check end edge (right side)
      if (Math.abs(position - endPos) <= edgeThreshold) {
        return { type: 'resize-end', index: i };
      }

      // Check if within slot body (between edges, excluding edge zones)
      if (position > startPos + edgeThreshold && position < endPos - edgeThreshold) {
        return { type: 'slot-body', index: i };
      }
    }

    return { type: 'empty', index: -1 };
  }, [timeToPosition, EDGE_THRESHOLD]);

  /**
   * Calculate the preview position for a moved slot
   * @param {Object} originalSlot - The slot being moved
   * @param {number} newStartPosition - The new start position (0-1)
   * @returns {{ start: string, end: string, startPos: number, endPos: number }}
   */
  const getMovedSlotPreview = useCallback((originalSlot, newStartPosition) => {
    const originalStartPos = timeToPosition(originalSlot.start);
    const originalEndPos = timeToPosition(originalSlot.end);
    const duration = originalEndPos - originalStartPos;

    // Calculate new positions with bounds checking
    let newStartPos = newStartPosition;
    let newEndPos = newStartPos + duration;

    // Clamp to timeline bounds (0-1)
    if (newStartPos < 0) {
      newStartPos = 0;
      newEndPos = duration;
    }
    if (newEndPos > 1) {
      newEndPos = 1;
      newStartPos = 1 - duration;
    }

    return {
      start: positionToTime(newStartPos),
      end: positionToTime(newEndPos),
      startPos: newStartPos,
      endPos: newEndPos
    };
  }, [timeToPosition, positionToTime]);

  /**
   * Calculate the preview for a resized slot
   * @param {Object} originalSlot - The slot being resized
   * @param {'start' | 'end'} edge - Which edge is being dragged
   * @param {number} newEdgePosition - The new position for the dragged edge (0-1)
   * @returns {{ start: string, end: string, startPos: number, endPos: number }}
   */
  const getResizedSlotPreview = useCallback((originalSlot, edge, newEdgePosition) => {
    const originalStartPos = timeToPosition(originalSlot.start);
    const originalEndPos = timeToPosition(originalSlot.end);
    const minDuration = 1 / TOTAL_HOURS; // 1 hour minimum as position value

    let newStartPos, newEndPos;

    if (edge === 'start') {
      newStartPos = Math.max(0, Math.min(newEdgePosition, originalEndPos - minDuration));
      newEndPos = originalEndPos;
    } else {
      newStartPos = originalStartPos;
      newEndPos = Math.min(1, Math.max(newEdgePosition, originalStartPos + minDuration));
    }

    return {
      start: positionToTime(newStartPos),
      end: positionToTime(newEndPos),
      startPos: newStartPos,
      endPos: newEndPos
    };
  }, [timeToPosition, positionToTime, TOTAL_HOURS]);

  // Format time for display - remove minutes when :00 and remove spaces
  const formatTime = useCallback((timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    // 24:00 is end-of-day midnight; treat it (and 00:00) as 12AM.
    const period = hours >= 12 && hours < 24 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    // Don't show minutes if they're :00
    if (minutes === 0) {
      return `${displayHours}${period}`;
    } else {
      return `${displayHours}:${String(minutes).padStart(2, '0')}${period}`;
    }
  }, []);

  // Format time range for timeline bars - RESPONSIVE FORMATTING
  const formatTimeRangeDesktop = useCallback((startTime, endTime) => {
    const startFormatted = formatTime(startTime);
    const endFormatted = formatTime(endTime);
    return `${startFormatted} - ${endFormatted}`;
  }, [formatTime]);

  const formatTimeRangeMobile = useCallback((startTime, endTime) => {
    const startFormatted = formatTime(startTime);
    const endFormatted = formatTime(endTime);
    return `${startFormatted} -\n${endFormatted}`;
  }, [formatTime]);

  // Format date label - UPDATED to use consistent format
  const formatDateLabel = useCallback((fullDateLabel) => {
    if (!isMobile) {
      // Desktop: Use full format like "Monday 30 June 2025"
      return fullDateLabel;
    }
    // Mobile: Use shortened format "Tue 16 Dec"
    const date = new Date(dateKey);
    const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${dayName} ${day} ${month}`;
  }, [isMobile, dateKey]);

  // Check if ranges overlap
  const rangesOverlap = useCallback((range1, range2) => {
    return range1.start < range2.end && range2.start < range1.end;
  }, []);

  // Merge overlapping ranges
  const mergeRanges = useCallback((ranges) => {
    if (ranges.length <= 1) return ranges;

    const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
    const merged = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const last = merged[merged.length - 1];

      if (rangesOverlap(last, current)) {
        last.end = current.end > last.end ? current.end : last.end;
      } else {
        merged.push(current);
      }
    }

    return merged;
  }, [rangesOverlap]);

  // Calculate duration of a time range in hours
  const calculateRangeDuration = useCallback((range) => {
    const [startHours, startMinutes] = range.start.split(':').map(Number);
    const [endHours, endMinutes] = range.end.split(':').map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    return (endTotalMinutes - startTotalMinutes) / 60;
  }, []);

  // Show disabled toast only once per second to prevent duplicates
  const showDisabledToast = useCallback(() => {
    const now = Date.now();
    if (now - lastDisabledToastTime.current > 1000) {
      lastDisabledToastTime.current = now;
      toast.info('Cannot change as calendar is connected. Go to Settings to disconnect.');
    }
  }, [toast]);

  // Handle mouse down - start drag selection
  const handleMouseDown = useCallback((event) => {
    // Don't allow editing if disabled (calendar sync active)
    if (disabled) {
      showDisabledToast();
      return;
    }

    event.preventDefault();
    const position = getPositionFromEvent(event);
    const time = positionToTime(position);
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;

    // Record interaction start for timing detection
    setTouchStartTime(Date.now());
    setTouchStartPosition(clientX);

    // Immediate visual feedback
    setTapPosition(position);
    setTapTime(time);

    // Detect interaction zone
    const zone = getInteractionZone(position, timeRanges, EDGE_THRESHOLD);

    if (zone.type === 'empty') {
      // Start new selection
      setIsDragging(true);
      setDragStart(time);
      setDragEnd(time);
      hapticImpact('light');
      lastHourTickRef.current = parseInt(time, 10);
    } else if (zone.type === 'slot-body') {
      // Don't delete immediately - wait to see if it's a tap or drag
      setPendingDeleteIndex(zone.index);
      lastHourTickRef.current = null;
    } else if (zone.type === 'resize-start' || zone.type === 'resize-end') {
      // Start resize mode
      setIsResizing(true);
      setResizingSlotIndex(zone.index);
      setResizeEdge(zone.type === 'resize-start' ? 'start' : 'end');
      setTapPosition(null); // Clear tap marker for resize
      setTapTime(null);
      hapticImpact('medium');
      const edgeTime = zone.type === 'resize-start' ? timeRanges[zone.index].start : timeRanges[zone.index].end;
      lastHourTickRef.current = parseInt(edgeTime, 10);
    }
  }, [disabled, showDisabledToast, getPositionFromEvent, positionToTime, timeRanges, getInteractionZone, EDGE_THRESHOLD]);

  // Handle mouse move - update drag selection and hover (desktop only)
  const handleMouseMove = useCallback((event) => {
    const position = getPositionFromEvent(event);
    const time = positionToTime(position);
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;

    // Only show hover time on desktop
    if (!isMobile) {
      setHoveredTime(time);

      // Track hover zone for cursor changes (desktop only, when not in an active operation)
      if (!isDragging && !isMoving && !isResizing && pendingDeleteIndex === null) {
        const zone = getInteractionZone(position, timeRanges, EDGE_THRESHOLD);
        setHoverZone(zone);
      }
    }

    // Check if this is the start of a drag on a pending delete
    if (pendingDeleteIndex !== null && touchStartPosition !== null) {
      const movement = Math.abs(clientX - touchStartPosition);
      const duration = touchStartTime ? Date.now() - touchStartTime : 0;

      // Initiate move if: movement > threshold OR hold time > threshold
      if (movement > MOVEMENT_THRESHOLD_PX || duration > TAP_THRESHOLD_MS) {
        const slot = timeRanges[pendingDeleteIndex];
        const slotStartPos = timeToPosition(slot.start);

        setIsMoving(true);
        setMovingSlotIndex(pendingDeleteIndex);
        setMoveOffset(position - slotStartPos); // Where within slot user grabbed
        setPendingDeleteIndex(null);
        setTapPosition(null);
        setTapTime(null);
        hapticImpact('medium');
        lastHourTickRef.current = parseInt(slot.start, 10);
      }
    }

    // Update move preview position
    if (isMoving && movingSlotIndex !== null) {
      const newStartPos = position - moveOffset;
      setMovePreviewPosition(newStartPos);
      const movedPreview = getMovedSlotPreview(timeRanges[movingSlotIndex], newStartPos);
      const movedHour = parseInt(movedPreview.start, 10);
      if (lastHourTickRef.current !== null && movedHour !== lastHourTickRef.current) {
        hapticImpact('light');
        lastHourTickRef.current = movedHour;
      }
    }

    // Update resize preview
    if (isResizing && resizingSlotIndex !== null && resizeEdge !== null) {
      const originalSlot = timeRanges[resizingSlotIndex];
      const preview = getResizedSlotPreview(originalSlot, resizeEdge, position);
      setResizePreview(preview);
      const edgeHour = parseInt(resizeEdge === 'start' ? preview.start : preview.end, 10);
      if (lastHourTickRef.current !== null && edgeHour !== lastHourTickRef.current) {
        hapticImpact('light');
        lastHourTickRef.current = edgeHour;
      }
    }

    if (isDragging && dragStart) {
      setDragEnd(time);
      const dragHour = parseInt(time, 10);
      if (lastHourTickRef.current !== null && dragHour !== lastHourTickRef.current) {
        hapticImpact('light');
        lastHourTickRef.current = dragHour;
      }
    }
  }, [getPositionFromEvent, positionToTime, isDragging, dragStart, isMobile, pendingDeleteIndex, touchStartPosition, touchStartTime, MOVEMENT_THRESHOLD_PX, TAP_THRESHOLD_MS, timeRanges, timeToPosition, isMoving, movingSlotIndex, moveOffset, isResizing, resizingSlotIndex, resizeEdge, getResizedSlotPreview, getMovedSlotPreview, getInteractionZone, EDGE_THRESHOLD]);

  // Handle mouse up - finalise selection
  const handleMouseUp = useCallback((event) => {
    const duration = touchStartTime ? Date.now() - touchStartTime : 0;
    const clientX = event?.changedTouches ? event.changedTouches[0].clientX : event?.clientX;
    const movement = touchStartPosition && clientX ? Math.abs(clientX - touchStartPosition) : 0;

    // Handle move finalisation
    if (isMoving && movingSlotIndex !== null && movePreviewPosition !== null) {
      const originalSlot = timeRanges[movingSlotIndex];
      const preview = getMovedSlotPreview(originalSlot, movePreviewPosition);

      // Create new ranges array with moved slot
      const newRanges = timeRanges.map((range, i) => {
        if (i === movingSlotIndex) {
          return { start: preview.start, end: preview.end };
        }
        return range;
      });

      // Merge any overlapping ranges
      const mergedRanges = mergeRanges(newRanges);
      onTimeRangesChange(mergedRanges);
      announce(`Moved time slot to ${formatTime(preview.start)} to ${formatTime(preview.end)}`);
      hapticSelection();
    }

    // Handle resize finalisation
    if (isResizing && resizingSlotIndex !== null && resizePreview !== null) {
      const newRanges = timeRanges.map((range, i) => {
        if (i === resizingSlotIndex) {
          return { start: resizePreview.start, end: resizePreview.end };
        }
        return range;
      });

      // Merge any overlapping ranges
      const mergedRanges = mergeRanges(newRanges);
      onTimeRangesChange(mergedRanges);
      announce(`Resized time slot to ${formatTime(resizePreview.start)} to ${formatTime(resizePreview.end)}`);
      hapticSelection();
    }

    // Handle pending delete (tap on slot body)
    if (pendingDeleteIndex !== null) {
      // If quick tap with minimal movement, delete the slot
      if (duration < TAP_THRESHOLD_MS && movement < MOVEMENT_THRESHOLD_PX) {
        const deletedSlot = timeRanges[pendingDeleteIndex];
        const newRanges = timeRanges.filter((_, i) => i !== pendingDeleteIndex);
        onTimeRangesChange(newRanges);
        announce(`Deleted time slot from ${formatTime(deletedSlot.start)} to ${formatTime(deletedSlot.end)}`);
        hapticImpact('medium');
      }
      // If longer hold or movement, move mode would have been initiated
      setPendingDeleteIndex(null);
    }

    // Handle new selection creation
    if (isDragging && dragStart && dragEnd) {
      const start = dragStart < dragEnd ? dragStart : dragEnd;
      const end = dragStart < dragEnd ? dragEnd : dragStart;

      if (start !== end) {
        const newRange = { start, end };
        const newRanges = mergeRanges([...timeRanges, newRange]);
        onTimeRangesChange(newRanges);
        announce(`Created time slot from ${formatTime(start)} to ${formatTime(end)}`);
        hapticSelection();
      }
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    setDraggedRange(null);
    // Clear move state
    setIsMoving(false);
    setMovingSlotIndex(null);
    setMoveOffset(0);
    setMovePreviewPosition(null);
    // Clear resize state
    setIsResizing(false);
    setResizingSlotIndex(null);
    setResizeEdge(null);
    setResizePreview(null);
    // Clear timing state
    setTouchStartTime(null);
    setTouchStartPosition(null);
    // Clear tap feedback
    setTapPosition(null);
    setTapTime(null);
    // Reset hour-tick tracker for next gesture
    lastHourTickRef.current = null;
  }, [isDragging, dragStart, dragEnd, timeRanges, mergeRanges, onTimeRangesChange, touchStartTime, touchStartPosition, pendingDeleteIndex, TAP_THRESHOLD_MS, MOVEMENT_THRESHOLD_PX, isMoving, movingSlotIndex, movePreviewPosition, getMovedSlotPreview, isResizing, resizingSlotIndex, resizePreview, announce, formatTime]);

  // Add event listeners for drag, move, resize, and pending delete operations
  useEffect(() => {
    if (isDragging || isMoving || isResizing || pendingDeleteIndex !== null) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = (e) => handleMouseUp(e);
      const handleGlobalTouchMove = (e) => {
        // Prevent page scrolling while dragging on timeline
        e.preventDefault();
        handleMouseMove(e);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalMouseUp);
      };
    }
  }, [isDragging, isMoving, isResizing, pendingDeleteIndex, handleMouseMove, handleMouseUp]);

  // Generate hour markers with mobile-specific positioning
  const hourMarkers = [];
  for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
    const position = ((hour - START_HOUR) / TOTAL_HOURS) * 100;
    // On mobile, only show labels every 6 hours (12am, 6am, 12pm, 6pm, 12am)
    const shouldShowLabel = isMobile ? (hour % 6 === 0) : (hour % 2 === 0); // Desktop: every 2 hours

    // Determine positioning classes based on position for mobile responsiveness
    let positionClasses;
    if (isMobile && shouldShowLabel) {
      if (hour === START_HOUR) {
        // First label (midnight) - left align on mobile, center on desktop
        positionClasses = 'left-0 sm:left-auto sm:transform sm:-translate-x-1/2';
      } else if (hour === END_HOUR) {
        // Last label (midnight) - right align on mobile, center on desktop
        positionClasses = 'right-0 sm:right-auto sm:transform sm:-translate-x-1/2';
      } else {
        // Middle labels - always centered
        positionClasses = 'transform -translate-x-1/2';
      }
    } else {
      // Desktop or non-mobile - always centered
      positionClasses = 'transform -translate-x-1/2';
    }

    hourMarkers.push({
      hour,
      position,
      label: formatTime(`${String(hour).padStart(2, '0')}:00`),
      showLabel: shouldShowLabel,
      positionClasses
    });
  }

  // Calculate total hours selected
  const totalHours = timeRanges.reduce((total, range) => {
    const startMinutes = timeToPosition(range.start) * TOTAL_HOURS * 60;
    const endMinutes = timeToPosition(range.end) * TOTAL_HOURS * 60;
    return total + (endMinutes - startMinutes) / 60;
  }, 0);

  // Get cursor class based on current state and hover zone
  const getCursorClass = () => {
    if (disabled) return 'cursor-not-allowed';
    if (isMoving) return 'cursor-grabbing';
    if (isResizing) return 'cursor-ew-resize';
    if (isMobile) return 'cursor-default';
    if (!hoverZone || hoverZone.type === 'empty') return 'cursor-crosshair';
    if (hoverZone.type === 'slot-body') return 'cursor-grab';
    if (hoverZone.type === 'resize-start' || hoverZone.type === 'resize-end') return 'cursor-ew-resize';
    return 'cursor-crosshair';
  };

  // Adjust slot by keyboard (hours change)
  const adjustSlotByKeyboard = useCallback((index, mode, hoursChange) => {
    const slot = timeRanges[index];
    if (!slot) return;

    const [startH] = slot.start.split(':').map(Number);
    const [endH] = slot.end.split(':').map(Number);

    let newStart = startH;
    let newEnd = endH;

    if (mode === 'move' || mode === null) {
      // Move entire slot
      newStart = startH + hoursChange;
      newEnd = endH + hoursChange;
    } else if (mode === 'resize-start') {
      // Adjust start time only
      newStart = startH + hoursChange;
    } else if (mode === 'resize-end') {
      // Adjust end time only
      newEnd = endH + hoursChange;
    }

    // Bounds checking
    if (newStart < START_HOUR || newEnd > END_HOUR) return;
    if (newEnd - newStart < 1) return; // Minimum 1 hour

    const newRanges = timeRanges.map((range, i) => {
      if (i === index) {
        return {
          start: `${String(newStart).padStart(2, '0')}:00`,
          end: `${String(newEnd).padStart(2, '0')}:00`
        };
      }
      return range;
    });

    onTimeRangesChange(mergeRanges(newRanges));
  }, [timeRanges, mergeRanges, onTimeRangesChange, START_HOUR, END_HOUR]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    if (disabled) return;

    const { key } = event;

    // Tab through slots
    if (key === 'Tab') {
      if (timeRanges.length === 0) return;
      event.preventDefault();

      let newIndex;
      if (event.shiftKey) {
        // Shift+Tab: previous slot
        newIndex = (selectedSlotIndex === null || selectedSlotIndex === 0)
          ? timeRanges.length - 1
          : selectedSlotIndex - 1;
      } else {
        // Tab: next slot
        newIndex = (selectedSlotIndex === null || selectedSlotIndex === timeRanges.length - 1)
          ? 0
          : selectedSlotIndex + 1;
      }
      setSelectedSlotIndex(newIndex);
      setKeyboardMode(null);
      const slot = timeRanges[newIndex];
      announce(`Selected time slot ${formatTime(slot.start)} to ${formatTime(slot.end)}. Press Enter to edit.`);
      return;
    }

    // No selected slot - ignore other keys
    if (selectedSlotIndex === null) return;

    // Enter: cycle through modes (move -> resize-start -> resize-end -> move)
    if (key === 'Enter') {
      event.preventDefault();
      let newMode;
      if (keyboardMode === null) newMode = 'move';
      else if (keyboardMode === 'move') newMode = 'resize-start';
      else if (keyboardMode === 'resize-start') newMode = 'resize-end';
      else newMode = 'move';
      setKeyboardMode(newMode);

      const modeMessages = {
        'move': 'Move mode. Use arrow keys to move slot.',
        'resize-start': 'Resize start mode. Use arrow keys to adjust start time.',
        'resize-end': 'Resize end mode. Use arrow keys to adjust end time.'
      };
      announce(modeMessages[newMode]);
      return;
    }

    // Escape: clear selection and mode
    if (key === 'Escape') {
      setSelectedSlotIndex(null);
      setKeyboardMode(null);
      announce('Selection cleared');
      return;
    }

    // Delete/Backspace: delete selected slot
    if (key === 'Delete' || key === 'Backspace') {
      event.preventDefault();
      const deletedSlot = timeRanges[selectedSlotIndex];
      const newRanges = timeRanges.filter((_, i) => i !== selectedSlotIndex);
      onTimeRangesChange(newRanges);
      setSelectedSlotIndex(null);
      setKeyboardMode(null);
      announce(`Deleted time slot from ${formatTime(deletedSlot.start)} to ${formatTime(deletedSlot.end)}`);
      return;
    }

    // Arrow keys: adjust slot based on current mode
    if (['ArrowLeft', 'ArrowRight'].includes(key)) {
      event.preventDefault();
      const direction = key === 'ArrowRight' ? 1 : -1;
      const hourChange = event.shiftKey ? 2 : 1; // Shift = 2 hour increments

      adjustSlotByKeyboard(selectedSlotIndex, keyboardMode, direction * hourChange);
    }
  }, [disabled, timeRanges, selectedSlotIndex, keyboardMode, onTimeRangesChange, adjustSlotByKeyboard, announce, formatTime]);

  // Only render timeline interface in advanced mode
  if (mode !== 'advanced') {
    return null;
  }

  // Get styling based on availability preference
  const getTimelineColors = () => {
    if (availabilityPreference === 'unavailable') {
      return {
        bgColor: 'bg-status-error',
        hoverColor: 'hover:brightness-90',
        dragColor: 'bg-status-error/50'
      };
    } else {
      return {
        bgColor: 'bg-accent',
        hoverColor: 'hover:bg-interactive-hover',
        dragColor: 'bg-accent/50'
      };
    }
  };

  const timelineColors = getTimelineColors();

  // Interactive cursor / marker / handle / preview colour - red in Set Busy mode, green in Set Available mode
  const isBusyMode = availabilityPreference === 'unavailable';
  const accentColourClass = isBusyMode ? 'bg-status-error' : 'bg-accent';
  const accentColourSoftClass = isBusyMode ? 'bg-status-error/50' : 'bg-accent/50';
  const accentColourBadgeSoftClass = isBusyMode ? 'bg-status-error/80' : 'bg-accent/80';
  const accentBorderClass = isBusyMode ? 'border-status-error' : 'border-accent';

  return (
    <div
      className="bg-card border border-border-subtle rounded-xl p-4 md:p-6 transition-all duration-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center">
          <h4 className="font-bold text-text-primary text-sm md:text-base">
            {formatDateLabel(dateLabel)}
          </h4>
        </div>
        {/* Action Buttons - Full Day when empty, Clear when populated.
            The previous "Copy" action (paste-from-source) has been removed
            because the bulk-paste flow above the day cards in MyAvailability
            covers the same intent more reliably. */}
        {!disabled && (() => {
          const hasData = timeRanges.length > 0;

          return (
            <div className="flex items-center space-x-1 md:space-x-2">
              {/* Full Day - shown only when no availability is set */}
              {!hasData && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onTimeRangesChange([{ start: '00:00', end: '24:00' }])}
                  className="flex items-center justify-center px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 bg-accent text-white hover:bg-interactive-hover"
                  title={availabilityPreference === 'available' ? 'Set full day available' : 'Block all day'}
                >
                  Full Day
                </motion.button>
              )}

              {/* Clear - shown only when day has availability */}
              {hasData && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onTimeRangesChange([])}
                  className="flex items-center justify-center px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 bg-status-error text-white hover:brightness-90"
                  title={availabilityPreference === 'available' ? 'Clear availability' : 'Clear busy times'}
                >
                  Clear
                </motion.button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Timeline */}
      <div className="space-y-3 md:space-y-4">
        {/* Hour Labels with Mobile-Specific Positioning */}
        <div className="relative h-4 md:h-6 px-4 sm:px-0">
          {hourMarkers.map(marker => (
            <div
              key={marker.hour}
              className={`absolute text-xs sm:text-sm text-text-muted font-medium ${marker.positionClasses}`}
              style={{
                left: marker.positionClasses.includes('left-0') ? '0' : marker.positionClasses.includes('right-0') ? 'auto' : `${marker.position}%`,
                right: marker.positionClasses.includes('right-0') ? '0' : 'auto'
              }}
            >
              {marker.showLabel ? marker.label : ''}
            </div>
          ))}
        </div>

        {/* Keyboard Mode Indicator */}
        {selectedSlotIndex !== null && (
          <div className="text-center text-xs text-text-muted mb-1">
            {keyboardMode === null && 'Slot selected • Press Enter to edit, ← → to move'}
            {keyboardMode === 'move' && 'Move mode • ← → to shift, Enter for resize'}
            {keyboardMode === 'resize-start' && 'Resize start • ← → to adjust start time'}
            {keyboardMode === 'resize-end' && 'Resize end • ← → to adjust end time'}
          </div>
        )}

        {/* Timeline Track with Mobile Padding and Increased Height */}
        <div className="px-4 sm:px-0">
          <div
            ref={timelineRef}
            className={`relative h-16 md:h-12 bg-surface rounded-lg select-none ${
              disabled ? 'opacity-50' : ''
            } ${getCursorClass()}`}
            onMouseDown={handleMouseDown}
            onMouseMove={disabled ? undefined : handleMouseMove}
            onMouseLeave={disabled ? undefined : () => {
              if (!isMobile) {
                setHoveredTime(null);
                setHoverZone(null);
              }
            }}
            onTouchStart={handleMouseDown}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setSelectedSlotIndex(null);
              setKeyboardMode(null);
            }}
            role="slider"
            aria-label={`Set ${availabilityPreference === 'available' ? 'availability' : 'busy times'} for ${dateLabel}. Use Tab to select slots, Enter to change mode, arrow keys to adjust.`}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
          >
            {/* Grid Lines */}
            {hourMarkers.map(marker => (
              <div
                key={`grid-${marker.hour}`}
                className="absolute top-0 bottom-0 w-px bg-border-subtle"
                style={{ left: `${marker.position}%` }}
              />
            ))}

            {/* Existing Time Ranges */}
            <AnimatePresence mode="popLayout">
              {timeRanges.map((range, index) => {
                const startPos = timeToPosition(range.start) * 100;
                const endPos = timeToPosition(range.end) * 100;
                const width = endPos - startPos;
                const duration = calculateRangeDuration(range);

                // Only show time text if range is wide enough (minimum 15% width for mobile, 10% for desktop)
                const minWidthForText = isMobile ? 15 : 10;
                const showTimeText = width >= minWidthForText;

                // Check if this slot is being manipulated or selected
                const isBeingMoved = isMoving && movingSlotIndex === index;
                const isBeingResized = isResizing && resizingSlotIndex === index;
                const isKeyboardSelected = selectedSlotIndex === index;
                const slotOpacity = isBeingMoved || isBeingResized ? 0.3 : 1;

                return (
                  <motion.div
                    key={`${range.start}-${range.end}-${index}`}
                    layout
                    layoutId={`slot-${dateKey}-${range.start}-${range.end}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: slotOpacity, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      layout: { duration: 0.2, ease: "easeOut" },
                      opacity: { duration: 0.15 },
                      scale: { duration: 0.15, delay: index * 0.02 }
                    }}
                    className={`absolute top-2 bottom-2 ${timelineColors.bgColor} rounded-md ${
                      disabled ? 'cursor-not-allowed' : `cursor-grab ${timelineColors.hoverColor}`
                    } ${isKeyboardSelected ? 'ring-2 ring-white ring-offset-1 ring-offset-surface' : ''}`}
                    style={{ left: `${startPos}%`, width: `${width}%` }}
                    title={disabled ? "" : "Drag to move, edges to resize, tap to delete"}
                  >
                    {/* RESPONSIVE Time Range Label - Two different elements */}
                    {showTimeText && (
                      <>
                        {/* Desktop Version - Single Line */}
                        <div className="hidden md:flex absolute inset-0 items-center justify-center p-1 overflow-hidden pointer-events-none">
                          <span className="text-sm text-white font-medium text-center">
                            {formatTimeRangeDesktop(range.start, range.end)}
                          </span>
                        </div>
                        {/* Mobile Version - With Line Break */}
                        <div className="flex md:hidden absolute inset-0 items-center justify-center p-1 overflow-hidden pointer-events-none">
                          <span className="text-xs text-white font-medium text-center leading-tight whitespace-pre-line">
                            {formatTimeRangeMobile(range.start, range.end)}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Mobile Edge Handles - Always Visible on mobile */}
                    {isMobile && !disabled && width >= 12 && (
                      <>
                        {/* Left edge handle indicator */}
                        <div
                          className={`absolute top-1/2 -translate-y-1/2 left-0.5 flex flex-col gap-0.5 transition-opacity duration-100 ${
                            isBeingResized && resizeEdge === 'start' ? 'opacity-100' : 'opacity-50'
                          }`}
                        >
                          <div className="w-0.5 h-1 bg-white/60 rounded-full" />
                          <div className="w-0.5 h-1 bg-white/60 rounded-full" />
                          <div className="w-0.5 h-1 bg-white/60 rounded-full" />
                        </div>

                        {/* Right edge handle indicator */}
                        <div
                          className={`absolute top-1/2 -translate-y-1/2 right-0.5 flex flex-col gap-0.5 transition-opacity duration-100 ${
                            isBeingResized && resizeEdge === 'end' ? 'opacity-100' : 'opacity-50'
                          }`}
                        >
                          <div className="w-0.5 h-1 bg-white/60 rounded-full" />
                          <div className="w-0.5 h-1 bg-white/60 rounded-full" />
                          <div className="w-0.5 h-1 bg-white/60 rounded-full" />
                        </div>
                      </>
                    )}

                    {/* Desktop Edge Highlights on Hover */}
                    {!isMobile && !disabled && (
                      <>
                        {/* Left edge highlight */}
                        <div
                          className={`absolute top-0 bottom-0 left-0 w-3 rounded-l-md transition-colors duration-100 ${
                            hoverZone?.type === 'resize-start' && hoverZone?.index === index
                              ? 'bg-white/20'
                              : ''
                          }`}
                        />
                        {/* Right edge highlight */}
                        <div
                          className={`absolute top-0 bottom-0 right-0 w-3 rounded-r-md transition-colors duration-100 ${
                            hoverZone?.type === 'resize-end' && hoverZone?.index === index
                              ? 'bg-white/20'
                              : ''
                          }`}
                        />
                      </>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Tap Marker - Anchor indicator showing where drag started */}
            {tapPosition !== null && isDragging && (
              <motion.div
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: 1, scaleY: 1 }}
                transition={{ duration: 0.05 }}
                className={`absolute top-0 bottom-0 w-0.5 z-20 ${
                  dragStart !== dragEnd ? accentColourSoftClass : accentColourClass
                }`}
                style={{ left: `${tapPosition * 100}%` }}
              >
                {/* Time Badge - only show when not yet dragged (preview labels handle it otherwise) */}
                {dragStart === dragEnd && (
                  <div className={`absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 ${accentColourClass} text-white rounded text-xs font-semibold whitespace-nowrap`}>
                    {tapTime && formatTime(tapTime)}
                  </div>
                )}
              </motion.div>
            )}

            {/* Current Drag Selection - shows immediately on drag start */}
            {isDragging && dragStart && dragEnd && (() => {
              const startPos = Math.min(timeToPosition(dragStart), timeToPosition(dragEnd)) * 100;
              const endPos = Math.max(timeToPosition(dragStart), timeToPosition(dragEnd)) * 100;
              const width = Math.max(0.5, endPos - startPos); // Minimum width for visibility
              const dragDirection = dragEnd > dragStart ? 'right' : 'left';
              // Calculate the final slot times for layoutId matching with committed slots
              const previewStart = dragStart < dragEnd ? dragStart : dragEnd;
              const previewEnd = dragStart < dragEnd ? dragEnd : dragStart;

              return (
                <>
                  {/* Selection area - layoutId matches committed slot format for smooth transition */}
                  <motion.div
                    layout
                    layoutId={`slot-${previewStart}-${previewEnd}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.7 }}
                    transition={{ layout: { duration: 0.2, ease: "easeOut" } }}
                    className={`absolute top-2 bottom-2 ${timelineColors.dragColor} rounded-md border-2 ${accentBorderClass} border-dashed`}
                    style={{
                      left: `${startPos}%`,
                      width: `${width}%`,
                      minWidth: '4px'
                    }}
                  >
                    {/* Time badges - single centred when start === end, or two at edges when different */}
                    {dragStart === dragEnd ? (
                      // Single centred label when user hasn't moved yet
                      <div className={`absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 ${accentColourClass} text-white rounded text-xs font-semibold whitespace-nowrap`}>
                        {formatTime(dragStart)}
                      </div>
                    ) : width < 5 ? (
                      // Combined label for narrow selections (< 5% width)
                      <div className={`absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 ${accentColourClass} text-white rounded text-xs font-semibold whitespace-nowrap`}>
                        {formatTime(dragStart < dragEnd ? dragStart : dragEnd)} - {formatTime(dragStart < dragEnd ? dragEnd : dragStart)}
                      </div>
                    ) : (
                      // Separate labels at edges for wider selections
                      <>
                        <div className={`absolute -top-6 left-0 px-1.5 py-0.5 ${accentColourBadgeSoftClass} text-white rounded text-xs font-medium whitespace-nowrap`}>
                          {formatTime(dragStart < dragEnd ? dragStart : dragEnd)}
                        </div>
                        <div className={`absolute -top-6 right-0 px-1.5 py-0.5 ${accentColourBadgeSoftClass} text-white rounded text-xs font-medium whitespace-nowrap`}>
                          {formatTime(dragStart < dragEnd ? dragEnd : dragStart)}
                        </div>
                      </>
                    )}
                  </motion.div>
                  {/* Current drag position cursor line */}
                  <div
                    className={`absolute top-0 bottom-0 w-0.5 ${accentColourClass} z-20`}
                    style={{ left: `${timeToPosition(dragEnd) * 100}%` }}
                  >
                    {/* Drag handle indicator */}
                    <div className={`absolute top-1/2 -translate-y-1/2 ${dragDirection === 'right' ? '-right-1' : '-left-1'} w-2 h-6 ${accentColourClass} rounded-full`} />
                  </div>
                </>
              );
            })()}

            {/* Move Preview */}
            {isMoving && movingSlotIndex !== null && movePreviewPosition !== null && (() => {
              const preview = getMovedSlotPreview(timeRanges[movingSlotIndex], movePreviewPosition);
              return (
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                  className={`absolute top-2 bottom-2 ${timelineColors.bgColor} rounded-md z-10`}
                  style={{
                    left: `${preview.startPos * 100}%`,
                    width: `${(preview.endPos - preview.startPos) * 100}%`
                  }}
                >
                  {/* Start time badge */}
                  <div className={`absolute -top-6 left-0 px-1.5 py-0.5 ${accentColourClass} text-white rounded text-xs font-semibold whitespace-nowrap`}>
                    {formatTime(preview.start)}
                  </div>
                  {/* End time badge */}
                  <div className={`absolute -top-6 right-0 px-1.5 py-0.5 ${accentColourClass} text-white rounded text-xs font-semibold whitespace-nowrap`}>
                    {formatTime(preview.end)}
                  </div>
                </motion.div>
              );
            })()}

            {/* Resize Preview */}
            {isResizing && resizingSlotIndex !== null && resizePreview !== null && (
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: 1 }}
                className={`absolute top-2 bottom-2 ${timelineColors.bgColor} rounded-md opacity-80 border-2 border-white z-10`}
                style={{
                  left: `${resizePreview.startPos * 100}%`,
                  width: `${(resizePreview.endPos - resizePreview.startPos) * 100}%`
                }}
              >
                {/* Time badge for edge being dragged */}
                <div className={`absolute -top-6 ${resizeEdge === 'start' ? 'left-0' : 'right-0'} px-1.5 py-0.5 ${accentColourClass} text-white rounded text-xs font-semibold whitespace-nowrap`}>
                  {formatTime(resizeEdge === 'start' ? resizePreview.start : resizePreview.end)}
                </div>
              </motion.div>
            )}

            {/* Hover Indicator - Desktop Only */}
            {hoveredTime && !isDragging && !isMobile && (
              <div
                className={`absolute top-0 bottom-0 w-px ${accentColourClass} opacity-50`}
                style={{ left: `${timeToPosition(hoveredTime) * 100}%` }}
              />
            )}
          </div>
        </div>

      </div>

      {/* Screen Reader Announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      {/* Hidden Instructions for Screen Readers */}
      <div id="timeline-instructions" className="sr-only">
        Use Tab to navigate between time slots.
        Press Enter to cycle between move and resize modes.
        Use arrow keys to adjust the selected slot.
        Press Delete to remove a slot.
        Tap or click on empty timeline to create new slots.
      </div>

    </div>
  );
}

export default TimelineAvailability;